/**
 * CallNotesEditor Component
 * Displays and allows editing of AI-generated call notes before adding to ServiceNow record.
 * Includes AI-suggested ticket attributes (subject, category, subcategory, priority).
 */

import { useState, useEffect } from 'react'
import { Send, Copy, RefreshCw, CheckCircle2, Loader2, Clock, AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Input } from '@/components/ui/input'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  useCallNotes,
  useTranscript,
  useTicketAttributeSuggestions,
  updateCallNotes,
  addNotesToTicket,
} from '@/hooks/useCallRecordings'

interface CallNotesEditorProps {
  recordingId: string
  sysId?: string
  table?: string
  onAddedToTicket?: () => void
}

export function CallNotesEditor({ recordingId, sysId, table, onAddedToTicket }: CallNotesEditorProps) {
  const { data: notes, isLoading: notesLoading } = useCallNotes(recordingId)
  const { data: transcript, isLoading: transcriptLoading } = useTranscript(recordingId)
  const { data: suggestions, isLoading: suggestionsLoading, error: suggestionsError } = useTicketAttributeSuggestions(
    recordingId,
    table
  )

  // Notes state
  const [editedNote, setEditedNote] = useState('')
  const [noteType, setNoteType] = useState<'work_note' | 'comment' | 'resolution'>('work_note')
  const [includeTranscript, setIncludeTranscript] = useState(false)
  const [hasEdited, setHasEdited] = useState(false)

  // Ticket attribute state
  const [ticketSubject, setTicketSubject] = useState('')
  // Whether to apply the AI-suggested summary as the record's short description.
  // Defaults ON (pre-filled), but the user can decline in one click to keep the
  // record's current short description.
  const [applySummary, setApplySummary] = useState(true)
  const [selectedPriority, setSelectedPriority] = useState('')
  const [selectedImpact, setSelectedImpact] = useState('')
  const [selectedUrgency, setSelectedUrgency] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('')
  const [selectedSubcategory, setSelectedSubcategory] = useState('')

  // Time entry state
  const [timeHours, setTimeHours] = useState('')
  const [includeTimeEntry, setIncludeTimeEntry] = useState(false)

  // Tab state
  const [activeTab, setActiveTab] = useState('attributes')

  // Action state
  const [isSaving, setIsSaving] = useState(false)
  const [isAddingToTicket, setIsAddingToTicket] = useState(false)
  const [actionMessage, setActionMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  // Initialize edited note when notes load
  useEffect(() => {
    if (notes?.formattedNote && !hasEdited) setEditedNote(notes.formattedNote)
  }, [notes, hasEdited])

  // Initialize ticket attributes when suggestions load
  useEffect(() => {
    if (suggestions) {
      if (!ticketSubject) setTicketSubject(suggestions.suggestedSubject || '')
      if (!selectedPriority && suggestions.suggestedPriority) setSelectedPriority(suggestions.suggestedPriority)
      if (!selectedImpact && suggestions.suggestedImpact) setSelectedImpact(suggestions.suggestedImpact)
      if (!selectedUrgency && suggestions.suggestedUrgency) setSelectedUrgency(suggestions.suggestedUrgency)
      if (!selectedCategory && suggestions.suggestedCategory) setSelectedCategory(suggestions.suggestedCategory)
      if (!selectedSubcategory && suggestions.suggestedSubcategory) setSelectedSubcategory(suggestions.suggestedSubcategory)
      if (!timeHours && suggestions.suggestedTimeHours) setTimeHours(String(suggestions.suggestedTimeHours))
    }
  }, [suggestions])

  // Reset subcategory when category changes
  useEffect(() => {
    if (selectedCategory && suggestions) {
      const validSubs = suggestions.availableSubcategories?.filter(
        s => !s.dependentValue || s.dependentValue === selectedCategory
      ) || []
      if (!validSubs.some(s => s.value === selectedSubcategory)) {
        setSelectedSubcategory('')
      }
    }
  }, [selectedCategory])

  const handleNoteChange = (value: string) => { setEditedNote(value); setHasEdited(true) }

  const handleSaveEdits = async () => {
    try {
      setIsSaving(true)
      await updateCallNotes(recordingId, editedNote)
      setActionMessage({ type: 'success', text: 'Notes saved' })
    } catch {
      setActionMessage({ type: 'error', text: 'Failed to save notes' })
    } finally {
      setIsSaving(false)
      setTimeout(() => setActionMessage(null), 3000)
    }
  }

  const handleAddToTicket = async () => {
    if (!sysId) {
      setActionMessage({ type: 'error', text: 'No record selected' })
      setTimeout(() => setActionMessage(null), 3000)
      return
    }

    try {
      setIsAddingToTicket(true)
      if (hasEdited) await updateCallNotes(recordingId, editedNote)

      // Build ticket field updates
      const ticketUpdates: Record<string, string> = {}
      if (applySummary && ticketSubject) ticketUpdates.short_description = ticketSubject
      if (selectedImpact) ticketUpdates.impact = selectedImpact
      if (selectedUrgency) ticketUpdates.urgency = selectedUrgency
      if (selectedCategory) ticketUpdates.category = selectedCategory
      if (selectedSubcategory) ticketUpdates.subcategory = selectedSubcategory

      await addNotesToTicket(recordingId, {
        sysId,
        table: table || 'incident',
        noteText: editedNote,
        noteType,
        includeTranscript,
        ticketUpdates: Object.keys(ticketUpdates).length > 0 ? ticketUpdates : undefined,
      })

      setActionMessage({ type: 'success', text: 'Notes and attributes added to record' })
      onAddedToTicket?.()
    } catch {
      setActionMessage({ type: 'error', text: 'Failed to add notes to record' })
    } finally {
      setIsAddingToTicket(false)
      setTimeout(() => setActionMessage(null), 3000)
    }
  }

  const handleCopy = async () => {
    await navigator.clipboard.writeText(editedNote)
    setActionMessage({ type: 'success', text: 'Copied to clipboard' })
    setTimeout(() => setActionMessage(null), 2000)
  }

  const handleResetToOriginal = () => {
    if (notes?.formattedNote) { setEditedNote(notes.formattedNote); setHasEdited(false) }
  }

  // Filter subcategories based on selected category
  const filteredSubcategories = suggestions?.availableSubcategories?.filter(
    s => !s.dependentValue || s.dependentValue === selectedCategory
  ) || []

  if (notesLoading) {
    return (
      <div className="space-y-4">
        <div className="h-8 w-48 bg-gray-200 dark:bg-slate-700 rounded animate-pulse" />
        <div className="h-32 w-full bg-gray-200 dark:bg-slate-700 rounded animate-pulse" />
      </div>
    )
  }

  if (!notes) {
    return <div className="text-center py-8 text-gray-500 dark:text-slate-400"><p>Notes not available yet.</p></div>
  }

  const sentimentColors: Record<string, string> = {
    satisfied: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
    neutral: 'bg-gray-100 text-gray-800 dark:bg-slate-700 dark:text-slate-300',
    frustrated: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
    concerned: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  }

  return (
    <div className="space-y-4 overflow-hidden">
      {actionMessage && (
        <div className={`flex items-center gap-2 p-2 rounded text-sm ${actionMessage.type === 'success' ? 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-300' : 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300'}`}>
          {actionMessage.type === 'success' ? <CheckCircle2 className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
          {actionMessage.text}
        </div>
      )}

      {/* Summary */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium dark:text-white">AI Summary</label>
          <span className={`text-xs px-2 py-0.5 rounded-full ${sentimentColors[notes.customerSentiment] || sentimentColors.neutral}`}>{notes.customerSentiment}</span>
        </div>
        <p className="text-sm text-gray-600 dark:text-slate-300">{notes.summary}</p>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full overflow-hidden">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="attributes" className="text-xs">Attributes</TabsTrigger>
          <TabsTrigger value="notes" className="text-xs">Notes</TabsTrigger>
          <TabsTrigger value="transcript" className="text-xs">Transcript</TabsTrigger>
        </TabsList>

        {/* Ticket Attributes Tab */}
        <TabsContent value="attributes" className="space-y-4">
          {suggestionsLoading ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-slate-400">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading AI suggestions...
              </div>
              <div className="h-10 w-full bg-gray-200 dark:bg-slate-700 rounded animate-pulse" />
              <div className="h-10 w-full bg-gray-200 dark:bg-slate-700 rounded animate-pulse" />
            </div>
          ) : suggestionsError ? (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm text-amber-600 dark:text-amber-400">
                <AlertCircle className="h-4 w-4" />
                <span>Could not load AI suggestions</span>
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium dark:text-white">Short Description</label>
                <Input
                  value={ticketSubject}
                  onChange={(e) => setTicketSubject(e.target.value)}
                  placeholder="Brief description of the issue"
                  maxLength={100}
                  className="dark:bg-slate-900 dark:border-slate-600"
                />
              </div>
            </div>
          ) : suggestions ? (
            <div className="space-y-4">
              <p className="text-xs text-gray-500 dark:text-slate-400">AI-suggested values - edit as needed</p>

              {/* Short Description */}
              <div className="space-y-1.5">
                <label className="text-sm font-medium dark:text-white">Short Description</label>
                <Input
                  value={ticketSubject}
                  onChange={(e) => setTicketSubject(e.target.value)}
                  placeholder="Brief description of the issue"
                  maxLength={100}
                  disabled={!applySummary}
                  className={`dark:bg-slate-900 dark:border-slate-600${applySummary ? '' : ' opacity-50'}`}
                />
                <p className="text-xs text-gray-400 dark:text-slate-500">{ticketSubject.length}/100 characters</p>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={applySummary}
                    onChange={(e) => setApplySummary(e.target.checked)}
                    className="rounded"
                  />
                  <span className="text-xs text-gray-600 dark:text-slate-300">
                    Apply this summary to the record
                    {!applySummary && <span className="text-amber-600 dark:text-amber-400"> — keeping current short description</span>}
                  </span>
                </label>
              </div>

              {/* Category */}
              {suggestions.availableCategories.length > 0 && (
                <div className="space-y-1.5">
                  <label className="text-sm font-medium dark:text-white">Category</label>
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="w-full h-9 border border-gray-300 dark:border-slate-600 rounded-md px-3 bg-white dark:bg-slate-900 text-sm text-gray-900 dark:text-white"
                  >
                    <option value="">Select category</option>
                    {suggestions.availableCategories.map((c) => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Subcategory */}
              {filteredSubcategories.length > 0 && (
                <div className="space-y-1.5">
                  <label className="text-sm font-medium dark:text-white">Subcategory</label>
                  <select
                    value={selectedSubcategory}
                    onChange={(e) => setSelectedSubcategory(e.target.value)}
                    className="w-full h-9 border border-gray-300 dark:border-slate-600 rounded-md px-3 bg-white dark:bg-slate-900 text-sm text-gray-900 dark:text-white"
                  >
                    <option value="">Select subcategory</option>
                    {filteredSubcategories.map((s) => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Impact & Urgency (ServiceNow derives priority from these) */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <label className="text-sm font-medium dark:text-white">Impact</label>
                  <select
                    value={selectedImpact}
                    onChange={(e) => setSelectedImpact(e.target.value)}
                    className="w-full h-9 border border-gray-300 dark:border-slate-600 rounded-md px-3 bg-white dark:bg-slate-900 text-sm text-gray-900 dark:text-white"
                  >
                    <option value="">Select</option>
                    {(suggestions.availableImpacts || []).map((i) => (
                      <option key={i.value} value={i.value}>{i.label}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium dark:text-white">Urgency</label>
                  <select
                    value={selectedUrgency}
                    onChange={(e) => setSelectedUrgency(e.target.value)}
                    className="w-full h-9 border border-gray-300 dark:border-slate-600 rounded-md px-3 bg-white dark:bg-slate-900 text-sm text-gray-900 dark:text-white"
                  >
                    <option value="">Select</option>
                    {(suggestions.availableUrgencies || []).map((u) => (
                      <option key={u.value} value={u.value}>{u.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              {suggestions.priorityReason && (
                <p className="text-xs text-gray-400 dark:text-slate-500 italic">{suggestions.priorityReason}</p>
              )}

              {/* Time Entry */}
              <div className="space-y-2 pt-2 border-t dark:border-slate-600">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="includeTimeEntry"
                    checked={includeTimeEntry}
                    onChange={(e) => setIncludeTimeEntry(e.target.checked)}
                    className="rounded"
                  />
                  <label htmlFor="includeTimeEntry" className="cursor-pointer text-sm flex items-center gap-1 dark:text-slate-300">
                    <Clock className="h-3 w-3" />
                    Add time entry
                  </label>
                </div>

                {includeTimeEntry && (
                  <div className="flex items-center gap-3 pl-5">
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        value={timeHours}
                        onChange={(e) => setTimeHours(e.target.value)}
                        step="0.25"
                        min="0.25"
                        className="w-20 h-8 text-sm dark:bg-slate-900 dark:border-slate-600"
                      />
                      <span className="text-sm text-gray-500 dark:text-slate-400">hours</span>
                    </div>
                    {suggestions.callDurationMinutes > 0 && (
                      <span className="text-xs text-gray-400 dark:text-slate-500">
                        ({suggestions.callDurationMinutes} min call)
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </TabsContent>

        {/* Notes Tab */}
        <TabsContent value="notes" className="space-y-4">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium dark:text-white">Formatted Note</label>
              <div className="flex gap-2">
                {hasEdited && <Button variant="ghost" size="sm" onClick={handleResetToOriginal}><RefreshCw className="h-4 w-4 mr-1" />Reset</Button>}
                <Button variant="ghost" size="sm" onClick={handleCopy}><Copy className="h-4 w-4 mr-1" />Copy</Button>
              </div>
            </div>
            <Textarea value={editedNote} onChange={(e) => handleNoteChange(e.target.value)} className="min-h-[200px] font-mono text-sm" placeholder="Edit the AI-generated notes here..." />
            {hasEdited && (
              <Button size="sm" onClick={handleSaveEdits} disabled={isSaving} className="bg-green-600 hover:bg-green-700 text-white">
                {isSaving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}Save Edits
              </Button>
            )}
          </div>

          <div className="space-y-3">
            <label className="text-sm font-medium dark:text-white">Note Type</label>
            <div className="flex gap-4 flex-wrap">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="noteType" value="work_note" checked={noteType === 'work_note'} onChange={() => setNoteType('work_note')} />
                <span className="text-sm dark:text-slate-300">Work Note</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="noteType" value="comment" checked={noteType === 'comment'} onChange={() => setNoteType('comment')} />
                <span className="text-sm dark:text-slate-300">Customer Comment</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="noteType" value="resolution" checked={noteType === 'resolution'} onChange={() => setNoteType('resolution')} />
                <span className="text-sm dark:text-slate-300">Resolution</span>
              </label>
            </div>
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={includeTranscript} onChange={(e) => setIncludeTranscript(e.target.checked)} className="rounded" />
            <span className="text-sm dark:text-slate-300">Include full transcript in note</span>
          </label>
        </TabsContent>

        {/* Transcript Tab */}
        <TabsContent value="transcript" className="space-y-4">
          {transcriptLoading ? (
            <div className="h-48 w-full bg-gray-200 dark:bg-slate-700 rounded animate-pulse" />
          ) : transcript ? (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium dark:text-white">Full Transcript</label>
                <span className="text-xs text-gray-400">{transcript.wordCount} words</span>
              </div>
              <div className="max-h-[300px] overflow-y-auto overflow-x-hidden rounded-md border dark:border-slate-600 p-4 bg-gray-50 dark:bg-slate-900">
                <p className="text-sm whitespace-pre-wrap break-words dark:text-slate-300">{transcript.text}</p>
              </div>
            </div>
          ) : (
            <p className="text-center text-gray-500 dark:text-slate-400 py-8">Transcript not available</p>
          )}
        </TabsContent>
      </Tabs>

      {/* Add to Record Button */}
      {sysId && !notes.addedToTicket && (
        <div className="pt-4 border-t dark:border-slate-600">
          <Button onClick={handleAddToTicket} disabled={isAddingToTicket} className="w-full gap-2 bg-blue-600 hover:bg-blue-700 text-white">
            {isAddingToTicket ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Update Record
          </Button>
        </div>
      )}

      {!sysId && (
        <div className="text-sm text-amber-600 dark:text-amber-400 pt-4 border-t dark:border-slate-600">
          No record associated. Copy the notes and add them manually.
        </div>
      )}

      {notes.addedToTicket && (
        <div className="flex items-center gap-2 text-green-600 dark:text-green-400 pt-4 border-t dark:border-slate-600">
          <CheckCircle2 className="h-5 w-5" />
          <span className="text-sm">Notes already added to record</span>
        </div>
      )}
    </div>
  )
}
